import {SelectChangeEvent, Stack, Typography} from '@mui/material';
import {translations} from 'presentation/translations-new';
import {Controller, useFormContext} from 'react-hook-form';
import React from 'react';
import type {RefundPlan} from 'domain/usecases';
import {PatientDataSchema} from '../../../../validations';
import {
	StyledPlanChip,
	StyledPlanFormControl,
	StyledPlanFormHelperText,
	StyledPlanLoading,
	StyledPlanMenuItem,
	StyledPlanPlaceholder,
	StyledPlanSelect,
	StyledPlanValidity,
} from './styles';
import {PlanSelectProps} from './types';

const {
	plan,
	planPlaceholder,
	notFound: planNotFound,
	active,
	inactive,
} = translations['pt-br'].privateFlow.refund.receiptRegistrationPage.planData;

const formatPlanDate = (date: string) => {
	const [year, month, day] = date.split('-');
	return `${day}/${month}/${year}`;
};

const isActivePlan = (option: RefundPlan) => !option.finalEffectiveDate;

const getPlanDateTime = (date: string) => new Date(date).getTime();

export const PlanSelect = ({
	planOptions,
	isPlanDisabled,
	isPlansFetching,
	hasPlanError,
	helperText,
}: PlanSelectProps) => {
	const {control, setValue, clearErrors} = useFormContext<PatientDataSchema>();
	const sortedPlanOptions = [...planOptions].sort((firstPlan, secondPlan) => {
		const isFirstPlanActive = isActivePlan(firstPlan);
		const isSecondPlanActive = isActivePlan(secondPlan);

		if (isFirstPlanActive && !isSecondPlanActive) {
			return -1;
		}

		if (!isFirstPlanActive && isSecondPlanActive) {
			return 1;
		}

		return (
			getPlanDateTime(secondPlan.initialEffectiveDate) -
			getPlanDateTime(firstPlan.initialEffectiveDate)
		);
	});

	const handleChangePlan = (
		event: SelectChangeEvent<unknown>,
		onChange: (selectedOption: PatientDataSchema['plan']) => void,
	) => {
		const selectedOption = sortedPlanOptions.find(
			option => String(option.planCode) === event.target.value,
		);
		onChange(selectedOption);
		setValue('serviceDate', '');
		clearErrors(['plan', 'serviceDate']);
	};

	const renderPlanOptions = () => {
		if (sortedPlanOptions.length === 0) {
			return (
				<StyledPlanMenuItem disabled value="">
					{planNotFound}
				</StyledPlanMenuItem>
			);
		}

		return sortedPlanOptions.map((option: RefundPlan) => {
			const isActive = isActivePlan(option);
			const statusLabel = isActive ? active : inactive;
			const statusColor: 'primary' | 'default' = isActive ? 'primary' : 'default';
			const validity = option.finalEffectiveDate
				? `${formatPlanDate(option.initialEffectiveDate)} até ${formatPlanDate(
						option.finalEffectiveDate,
					)}`
				: '';

			return (
				<StyledPlanMenuItem key={option.planCode} value={String(option.planCode)}>
					<StyledPlanChip label={statusLabel} color={statusColor} size="small" />
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						gap={2}
						width="100%">
						<Typography color="text.primary">{option.planName}</Typography>
						{validity ? (
							<StyledPlanValidity>{validity}</StyledPlanValidity>
						) : null}
					</Stack>
				</StyledPlanMenuItem>
			);
		});
	};

	return (
		<Stack gap={1} flex={1}>
			<Typography color="primary" fontWeight={600}>
				{plan}
			</Typography>

			<Controller
				control={control}
				name="plan"
				render={({field: {value, onChange}}) => (
					<StyledPlanFormControl error={hasPlanError}>
						<StyledPlanSelect
							disabled={isPlanDisabled}
							displayEmpty
							value={value?.planCode ? String(value.planCode) : ''}
							IconComponent={
								isPlansFetching ? () => <StyledPlanLoading size={20} /> : undefined
							}
							onChange={event => handleChangePlan(event, onChange)}
							renderValue={selected => {
								const selectedPlan = sortedPlanOptions.find(
									option => String(option.planCode) === selected,
								);

								if (selectedPlan) {
									return selectedPlan.planName;
								}

								return (
									<StyledPlanPlaceholder $hasError={hasPlanError}>
										{planPlaceholder}
									</StyledPlanPlaceholder>
								);
							}}>
							{renderPlanOptions()}
						</StyledPlanSelect>
						<StyledPlanFormHelperText>{helperText}</StyledPlanFormHelperText>
					</StyledPlanFormControl>
				)}
			/>
		</Stack>
	);
};
